import {FC, useRef, useState, useEffect, useContext} from "react";
import * as d3 from "d3";
import {
  IPeople,
  IPrevPeople,
  IinitiativeAllInfo,
  IInitiativeParticipant,
} from "../../../pages/StateInitiative/StateInitiative.type";
import renderLogoImage from "../../RadarChart/components/LogoImage";
import {ITypeRadar} from "../../../types";
import useFetchDataLogotypeGpb from "../../../hooks/useFetchDataLogotypeGpb";
import useFetchDataStateInitiativeById from "../../../hooks/useFetchDataStateInitiativeById";
import {DataStateInitiativeIdsContext} from "../../../pages/StateInitiative/StateInitiative";

interface IProps {
  onClose: () => void;
  documentId?: string; // Добавляем documentId как проп

  arrPersonsWorker?: IPeople[];
  setIdWorkerShowConnections?: (id: string) => void;
  prevIdWorker?: IPrevPeople[];
  setPrevIdWorker?: (PrevPeople: IPrevPeople[]) => void;
  typeRadar?: ITypeRadar;
  initiativeData?: IinitiativeAllInfo | null;
}
const dimensions = {width: window.innerWidth, height: window.innerHeight};
const widthWrapper = dimensions.width - 630;
const delta = dimensions.height / 1440;
const OUTER_DIAM = 500 * delta;
const SVG_PADDING = 300 * delta;
const SVG_WIDTH = OUTER_DIAM + (widthWrapper - OUTER_DIAM);
const SVG_DIAM = OUTER_DIAM + SVG_PADDING * 2;
const PHOTO_DIAM = 100 * delta;

export const SidebarCardCrgci: FC<IProps> = ({
  onClose,
  documentId: propDocumentId,
  arrPersonsWorker,
  setIdWorkerShowConnections,
  prevIdWorker,
  setPrevIdWorker,
  typeRadar = "fintech",
  initiativeData,
}) => {
  const {settingData} = useFetchDataLogotypeGpb();
  const isShowLogotypeGpb = settingData?.data?.showGpb || false;

  const {dataIds} = useContext(DataStateInitiativeIdsContext) || {};
  const contextDocumentId = dataIds?.trendId;

  const documentId = propDocumentId || contextDocumentId;

  const {
    initiativeData: apiInitiativeData,
    loading,
    error,
  } = useFetchDataStateInitiativeById(
    typeof documentId === "string" ? documentId : null
  );

  const finalInitiativeData = apiInitiativeData || initiativeData;

  const svgRef = useRef<SVGSVGElement | null>(null);
  const [hoveredPoint, setHoveredPoint] = useState<string | null>(null);

  const createSectorPath = (
    startAngle: number,
    endAngle: number,
    innerRadius: number,
    outerRadius: number
  ) => {
    const centerX = SVG_WIDTH / 2;
    const centerY = SVG_DIAM / 2;

    const startAngleRad = (startAngle * Math.PI) / 180;
    const endAngleRad = (endAngle * Math.PI) / 180;

    const x1 = centerX + innerRadius * Math.cos(startAngleRad);
    const y1 = centerY + innerRadius * Math.sin(startAngleRad);
    const x2 = centerX + outerRadius * Math.cos(startAngleRad);
    const y2 = centerY + outerRadius * Math.sin(startAngleRad);

    const x3 = centerX + outerRadius * Math.cos(endAngleRad);
    const y3 = centerY + outerRadius * Math.sin(endAngleRad);
    const x4 = centerX + innerRadius * Math.cos(endAngleRad);
    const y4 = centerY + innerRadius * Math.sin(endAngleRad);

    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    return [
      "M",
      x1,
      y1,
      "L",
      x2,
      y2,
      "A",
      outerRadius,
      outerRadius,
      0,
      largeArcFlag,
      1,
      x3,
      y3,
      "L",
      x4,
      y4,
      "A",
      innerRadius,
      innerRadius,
      0,
      largeArcFlag,
      0,
      x1,
      y1,
      "Z",
    ].join(" ");
  };

  const createSectorPoints = (
    startAngle: number,
    endAngle: number,
    pointCount: number
  ) => {
    const centerX = SVG_WIDTH / 2;
    const centerY = SVG_DIAM / 2;
    const middleRadius = (OUTER_DIAM + PHOTO_DIAM) / 2;

    const sectorAngle = endAngle - startAngle;
    const points = [];

    if (pointCount > 0) {
      const subSectorAngle = sectorAngle / pointCount;

      for (let i = 0; i < pointCount; i++) {
        const subSectorCenter = startAngle + (i + 0.5) * subSectorAngle;
        const angleRad = (subSectorCenter * Math.PI) / 180;

        points.push({
          x: centerX + middleRadius * Math.cos(angleRad),
          y: centerY + middleRadius * Math.sin(angleRad),
        });
      }
    }

    return points;
  };

  // Функция для создания динамического sectorData из participants
  const createDynamicSectorData = () => {
    // Получаем участников из API данных
    const participants = finalInitiativeData?.participants || [];

    // Маппинг названий сегментов к индексам секторов (соответствует визуальному отображению)
    const segmentToSectorMap: {[key: string]: number} = {
      "Ассоциативные площадки": 1, // arc-sector1 (180°-225°)
      "Банк России": 2, // arc-sector2 (225°-270°)
      "Органы власти": 3, // arc-sector3 (270°-315°)
      Рынок: 4, // arc-sector4 (315°-360°)
      ГПБ: 0, // Нет визуального сектора, используем индекс 0 (0°-180°)
    };

    // Инициализируем массив для каждого сектора (соответствует визуальным секторам)
    const sectorParticipants: {
      [key: number]: {
        labels: string[];
        fullNames: string[];
        departments: string[];
        jobFunctions: string[];
        documentIds: string[];
      };
    } = {
      0: {
        labels: [],
        fullNames: [],
        departments: [],
        jobFunctions: [],
        documentIds: [],
      }, // ГПБ (0°-180°)
      1: {
        labels: [],
        fullNames: [],
        departments: [],
        jobFunctions: [],
        documentIds: [],
      },
      2: {
        labels: [],
        fullNames: [],
        departments: [],
        jobFunctions: [],
        documentIds: [],
      },
      3: {
        labels: [],
        fullNames: [],
        departments: [],
        jobFunctions: [],
        documentIds: [],
      },
      4: {
        labels: [],
        fullNames: [],
        departments: [],
        jobFunctions: [],
        documentIds: [],
      },
    };

    if (Array.isArray(participants)) {
      participants.forEach((participant: IInitiativeParticipant) => {
        const segmentName = participant.person?.organization?.segment?.name;
        const fullName = participant.person?.fullName;
        const department = participant.person?.department;
        const jobFunction = participant.person?.jobFunction;
        const documentId = participant.person?.documentId;

        if (segmentName && fullName && documentId) {
          const sectorIndex = segmentToSectorMap[segmentName];

          if (sectorIndex !== undefined) {
            const nameParts = fullName.split(" ");
            const surname = nameParts[0] || fullName;

            sectorParticipants[sectorIndex].labels.push(surname);
            sectorParticipants[sectorIndex].fullNames.push(fullName);
            sectorParticipants[sectorIndex].departments.push(department || "");
            sectorParticipants[sectorIndex].jobFunctions.push(
              jobFunction || ""
            );
            sectorParticipants[sectorIndex].documentIds.push(documentId);
          }
        }
      });
    }

    // Возвращаем sectorData с правильным соответствием визуальным секторам
    return [
      {
        startAngle: 0,
        endAngle: 180,
        pointCount: sectorParticipants[0].labels.length, // ГПБ
        labels: sectorParticipants[0].labels,
        fullNames: sectorParticipants[0].fullNames,
        departments: sectorParticipants[0].departments,
        jobFunctions: sectorParticipants[0].jobFunctions,
        documentIds: sectorParticipants[0].documentIds,
      },
      {
        startAngle: 180,
        endAngle: 225,
        pointCount: sectorParticipants[1].labels.length, // Ассоциативные площадки
        labels: sectorParticipants[1].labels,
        fullNames: sectorParticipants[1].fullNames,
        departments: sectorParticipants[1].departments,
        jobFunctions: sectorParticipants[1].jobFunctions,
        documentIds: sectorParticipants[1].documentIds,
      },
      {
        startAngle: 225,
        endAngle: 270,
        pointCount: sectorParticipants[2].labels.length, // Банк России
        labels: sectorParticipants[2].labels,
        fullNames: sectorParticipants[2].fullNames,
        departments: sectorParticipants[2].departments,
        jobFunctions: sectorParticipants[2].jobFunctions,
        documentIds: sectorParticipants[2].documentIds,
      },
      {
        startAngle: 270,
        endAngle: 315,
        pointCount: sectorParticipants[3].labels.length, // Органы власти
        labels: sectorParticipants[3].labels,
        fullNames: sectorParticipants[3].fullNames,
        departments: sectorParticipants[3].departments,
        jobFunctions: sectorParticipants[3].jobFunctions,
        documentIds: sectorParticipants[3].documentIds,
      },
      {
        startAngle: 315,
        endAngle: 360,
        pointCount: sectorParticipants[4].labels.length, // Рынок
        labels: sectorParticipants[4].labels,
        fullNames: sectorParticipants[4].fullNames,
        departments: sectorParticipants[4].departments,
        jobFunctions: sectorParticipants[4].jobFunctions,
        documentIds: sectorParticipants[4].documentIds,
      },
    ];
  };

  const sectorData = createDynamicSectorData();

  const connectionsData = {
    centerConnections: ["Волков", "Лебедев", "Петров", "Сидоров", "Новиков"],

    peerConnections: [
      {person1: "Морозов", person2: "Новиков"},
      {person1: "Козлов", person2: "Сидоров"},
      {person1: "Сидоров", person2: "Петров"},
      {person1: "Петров", person2: "Иванов"},
    ],
  };

  const findAllConnectedPoints = (targetSurname: string): string[] => {
    const visited = new Set<string>();
    const connected = new Set<string>();

    const findConnections = (surname: string) => {
      if (visited.has(surname)) return;
      visited.add(surname);
      connected.add(surname);

      connectionsData.peerConnections.forEach((connection) => {
        if (
          connection.person1.includes(surname) &&
          !visited.has(connection.person2)
        ) {
          findConnections(connection.person2);
        }
        if (
          connection.person2.includes(surname) &&
          !visited.has(connection.person1)
        ) {
          findConnections(connection.person1);
        }
      });

      if (
        connectionsData.centerConnections.some((name) => name.includes(surname))
      ) {
        connected.add(surname);
      }
    };

    findConnections(targetSurname);
    return Array.from(connected);
  };

  const isPointConnected = (
    labelText: string,
    hoveredPointId: string | null
  ): boolean => {
    if (!hoveredPointId) return false;

    let hoveredSurname = "";
    sectorData.forEach((sector) => {
      sector.labels?.forEach((label, index) => {
        const pointId = `sector-${sectorData.indexOf(sector)}-point-${index}`;
        if (pointId === hoveredPointId) {
          hoveredSurname = label.split(" ")[0];
        }
      });
    });

    if (!hoveredSurname) return false;

    const connectedPoints = findAllConnectedPoints(hoveredSurname);

    const currentSurname = labelText.split(" ")[0];
    return connectedPoints.some((surname) => surname.includes(currentSurname));
  };

  const createLabelPosition = (
    pointX: number,
    pointY: number,
    labelText: string
  ) => {
    const centerX = SVG_WIDTH / 2;
    const centerY = SVG_DIAM / 2;
    const currentRadius = (OUTER_DIAM + PHOTO_DIAM) / 2;

    const isProblematicLabel =
      labelText.includes("Петров") ||
      labelText.includes("Морозов") ||
      labelText.includes("Новиков") ||
      labelText.includes("Волков");

    const labelRadius =
      currentRadius + (isProblematicLabel ? 50 * delta : 30 * delta);

    const angle = Math.atan2(pointY - centerY, pointX - centerX);

    const labelX = centerX + labelRadius * Math.cos(angle);
    const labelY = centerY + labelRadius * Math.sin(angle);

    const isRightHalf = pointX > centerX;

    return {
      x: labelX,
      y: labelY,
      isRightHalf,
    };
  };

  useEffect(() => {
    if (svgRef.current) {
      const svg = d3.select(svgRef.current);

      svg.select(".logo-group").remove();

      const logoGroup = svg
        .append("g")
        .attr("class", "logo-group")
        .attr("transform", `translate(${SVG_WIDTH / 2}, ${SVG_DIAM / 2})`);

      const logoRadius = PHOTO_DIAM / 2;
      renderLogoImage(logoGroup, isShowLogotypeGpb, logoRadius, typeRadar);
    }
  }, [isShowLogotypeGpb, typeRadar]);

  return (
    <>
      <div
        style={{
          position: "absolute",
          left: "600px",
          bottom: 0,
          top: 0,
          background: "#0C0C0C",
          color: "#fff",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          borderRadius: "16px",
          padding: "16px",
          zIndex: 20,

          width: `${widthWrapper}px`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{minWidth: SVG_WIDTH}}>
          <svg
            ref={svgRef}
            width={SVG_WIDTH}
            height={SVG_DIAM}
            style={{display: "block", margin: "0 auto"}}
          >
            <defs>
              <path
                id="arc-bottom"
                d={`M ${
                  SVG_WIDTH / 2 -
                  (OUTER_DIAM + 40 * delta) * Math.cos((Math.PI * 45) / 180)
                } ${
                  SVG_DIAM / 2 +
                  (OUTER_DIAM + 40 * delta) * Math.sin((Math.PI * 45) / 180)
                } A ${OUTER_DIAM + 40 * delta} ${
                  OUTER_DIAM + 40 * delta
                } 0 0 0 ${
                  SVG_WIDTH / 2 +
                  (OUTER_DIAM + 40 * delta) * Math.cos((Math.PI * 45) / 180)
                } ${
                  SVG_DIAM / 2 +
                  (OUTER_DIAM + 40 * delta) * Math.sin((Math.PI * 45) / 180)
                }`}
              />
              <path
                id="arc-sector1"
                d={`M ${
                  SVG_WIDTH / 2 +
                  (OUTER_DIAM + 20 * delta) * Math.cos((Math.PI * 180) / 180)
                } ${
                  SVG_DIAM / 2 +
                  (OUTER_DIAM + 20 * delta) * Math.sin((Math.PI * 180) / 180)
                } A ${OUTER_DIAM + 20 * delta} ${
                  OUTER_DIAM + 20 * delta
                } 0 0 1 ${
                  SVG_WIDTH / 2 +
                  (OUTER_DIAM + 20 * delta) * Math.cos((Math.PI * 225) / 180)
                } ${
                  SVG_DIAM / 2 +
                  (OUTER_DIAM + 20 * delta) * Math.sin((Math.PI * 225) / 180)
                }`}
              />
              <path
                id="arc-sector2"
                d={`M ${
                  SVG_WIDTH / 2 +
                  (OUTER_DIAM + 20 * delta) * Math.cos((Math.PI * 225) / 180)
                } ${
                  SVG_DIAM / 2 +
                  (OUTER_DIAM + 20 * delta) * Math.sin((Math.PI * 225) / 180)
                } A ${OUTER_DIAM + 20 * delta} ${
                  OUTER_DIAM + 20 * delta
                } 0 0 1 ${
                  SVG_WIDTH / 2 +
                  (OUTER_DIAM + 20 * delta) * Math.cos((Math.PI * 270) / 180)
                } ${
                  SVG_DIAM / 2 +
                  (OUTER_DIAM + 20 * delta) * Math.sin((Math.PI * 270) / 180)
                }`}
              />
              <path
                id="arc-sector3"
                d={`M ${
                  SVG_WIDTH / 2 +
                  (OUTER_DIAM + 20 * delta) * Math.cos((Math.PI * 270) / 180)
                } ${
                  SVG_DIAM / 2 +
                  (OUTER_DIAM + 20 * delta) * Math.sin((Math.PI * 270) / 180)
                } A ${OUTER_DIAM + 20 * delta} ${
                  OUTER_DIAM + 20 * delta
                } 0 0 1 ${
                  SVG_WIDTH / 2 +
                  (OUTER_DIAM + 20 * delta) * Math.cos((Math.PI * 315) / 180)
                } ${
                  SVG_DIAM / 2 +
                  (OUTER_DIAM + 20 * delta) * Math.sin((Math.PI * 315) / 180)
                }`}
              />
              <path
                id="arc-sector4"
                d={`M ${
                  SVG_WIDTH / 2 +
                  (OUTER_DIAM + 20 * delta) * Math.cos((Math.PI * 315) / 180)
                } ${
                  SVG_DIAM / 2 +
                  (OUTER_DIAM + 20 * delta) * Math.sin((Math.PI * 315) / 180)
                } A ${OUTER_DIAM + 20 * delta} ${
                  OUTER_DIAM + 20 * delta
                } 0 0 1 ${
                  SVG_WIDTH / 2 +
                  (OUTER_DIAM + 20 * delta) * Math.cos((Math.PI * 360) / 180)
                } ${
                  SVG_DIAM / 2 +
                  (OUTER_DIAM + 20 * delta) * Math.sin((Math.PI * 360) / 180)
                }`}
              />
            </defs>

            <path
              d={createSectorPath(0, 180, PHOTO_DIAM, OUTER_DIAM)}
              fill="rgba(17, 160, 218, 0.2)"
              stroke="rgba(17, 160, 218, 0.5)"
              strokeWidth={2 * delta}
            />

            <path
              d={createSectorPath(180, 225, PHOTO_DIAM, OUTER_DIAM)}
              fill="rgba(17, 160, 218, 0.2)"
              stroke="rgba(17, 160, 218, 0.5)"
              strokeWidth={2 * delta}
            />
            <path
              d={createSectorPath(225, 270, PHOTO_DIAM, OUTER_DIAM)}
              fill="rgba(17, 160, 218, 0.2)"
              stroke="rgba(17, 160, 218, 0.5)"
              strokeWidth={2 * delta}
            />
            <path
              d={createSectorPath(270, 315, PHOTO_DIAM, OUTER_DIAM)}
              fill="rgba(17, 160, 218, 0.2)"
              stroke="rgba(17, 160, 218, 0.5)"
              strokeWidth={2 * delta}
            />
            <path
              d={createSectorPath(315, 360, PHOTO_DIAM, OUTER_DIAM)}
              fill="rgba(17, 160, 218, 0.2)"
              stroke="rgba(17, 160, 218, 0.5)"
              strokeWidth={2 * delta}
            />

            <circle
              cx={SVG_WIDTH / 2}
              cy={SVG_DIAM / 2}
              r={PHOTO_DIAM}
              fill="none"
              stroke="rgba(17, 160, 218, 0.5)"
              strokeWidth={2 * delta}
            />

            <text
              fontSize={40 * delta}
              fill="rgba(17, 160, 218, 0.83)"
              textAnchor="middle"
            >
              <textPath href="#arc-bottom" startOffset="50%">
                ГПБ
              </textPath>
            </text>

            <text
              fontSize={34 * delta}
              fill="rgba(17, 160, 218, 0.83)"
              textAnchor="middle"
            >
              <textPath href="#arc-sector1" startOffset="50%">
                Ассоциативные площадки
              </textPath>
            </text>

            <text
              fontSize={34 * delta}
              fill="rgba(17, 160, 218, 0.83)"
              textAnchor="middle"
            >
              <textPath href="#arc-sector2" startOffset="50%">
                Банк России
              </textPath>
            </text>

            <text
              fontSize={34 * delta}
              fill="rgba(17, 160, 218, 0.83)"
              textAnchor="middle"
            >
              <textPath href="#arc-sector3" startOffset="50%">
                Органы власти
              </textPath>
            </text>

            <text
              fontSize={34 * delta}
              fill="rgba(17, 160, 218, 0.83)"
              textAnchor="middle"
            >
              <textPath href="#arc-sector4" startOffset="50%">
                Рынок
              </textPath>
            </text>

            {sectorData.map((sector, sectorIndex) => {
              const points = createSectorPoints(
                sector.startAngle,
                sector.endAngle,
                sector.pointCount
              );
              return points.map((point, pointIndex) => {
                const labelText = sector.labels?.[pointIndex] || "";

                const hasConnectionToCenter =
                  connectionsData.centerConnections.some((surname) =>
                    labelText.includes(surname)
                  );

                if (hasConnectionToCenter) {
                  const centerX = SVG_WIDTH / 2;
                  const centerY = SVG_DIAM / 2;
                  const innerRadius = PHOTO_DIAM;

                  const angle = Math.atan2(
                    point.y - centerY,
                    point.x - centerX
                  );
                  const innerX = centerX + innerRadius * Math.cos(angle);
                  const innerY = centerY + innerRadius * Math.sin(angle);

                  return (
                    <line
                      key={`dashed-line-${sectorIndex}-${pointIndex}`}
                      x1={point.x}
                      y1={point.y}
                      x2={innerX}
                      y2={innerY}
                      stroke="#11A0DA"
                      strokeWidth={2 * delta}
                      strokeDasharray={`${8 * delta},${4 * delta}`}
                      style={{
                        transition: "all 0.3s ease",
                      }}
                    />
                  );
                }
                return null;
              });
            })}

            {(() => {
              const targetSector = sectorData.find(
                (sector) =>
                  sector.labels?.includes("Морозов Л.М.") &&
                  sector.labels?.includes("Новиков Н.О.")
              );

              if (targetSector) {
                const points = createSectorPoints(
                  targetSector.startAngle,
                  targetSector.endAngle,
                  targetSector.pointCount
                );

                const morozovIndex =
                  targetSector.labels?.indexOf("Морозов Л.М.");
                const novikovIndex =
                  targetSector.labels?.indexOf("Новиков Н.О.");

                if (
                  morozovIndex !== undefined &&
                  novikovIndex !== undefined &&
                  morozovIndex !== -1 &&
                  novikovIndex !== -1
                ) {
                  const morozovPoint = points[morozovIndex];
                  const novikovPoint = points[novikovIndex];

                  return (
                    <line
                      key="morozov-novikov-connection"
                      x1={morozovPoint.x}
                      y1={morozovPoint.y}
                      x2={novikovPoint.x}
                      y2={novikovPoint.y}
                      stroke="#11A0DA"
                      strokeWidth={2 * delta}
                      strokeDasharray={`${8 * delta},${4 * delta}`}
                      style={{
                        transition: "all 0.3s ease",
                      }}
                    />
                  );
                }
              }
              return null;
            })()}

            {(() => {
              const lines: JSX.Element[] = [];

              connectionsData.peerConnections.forEach((connection) => {
                let point1: {x: number; y: number} | undefined;
                let point2: {x: number; y: number} | undefined;

                sectorData.forEach((sector) => {
                  const points = createSectorPoints(
                    sector.startAngle,
                    sector.endAngle,
                    sector.pointCount
                  );

                  sector.labels?.forEach((label, pointIndex) => {
                    if (label.includes(connection.person1)) {
                      point1 = points[pointIndex];
                    }
                    if (label.includes(connection.person2)) {
                      point2 = points[pointIndex];
                    }
                  });
                });

                if (point1 && point2) {
                  lines.push(
                    <line
                      key={`connection-${connection.person1}-${connection.person2}`}
                      x1={point1.x}
                      y1={point1.y}
                      x2={point2.x}
                      y2={point2.y}
                      stroke="#11A0DA"
                      strokeWidth={2 * delta}
                      strokeDasharray={`${8 * delta},${4 * delta}`}
                      style={{
                        transition: "all 0.3s ease",
                      }}
                    />
                  );
                }
              });

              return lines;
            })()}

            {sectorData.map((sector, sectorIndex) => {
              const points = createSectorPoints(
                sector.startAngle,
                sector.endAngle,
                sector.pointCount
              );
              return points.map((point, pointIndex) => {
                const pointId = `sector-${sectorIndex}-point-${pointIndex}`;
                const isHovered = hoveredPoint === pointId;
                const labelText = sector.labels?.[pointIndex] || "";
                const isConnected = isPointConnected(labelText, hoveredPoint);
                const documentId = sector.documentIds?.[pointIndex];

                return (
                  <circle
                    key={pointId}
                    cx={point.x}
                    cy={point.y}
                    r={15 * delta}
                    fill={isHovered || isConnected ? "white" : "#86D1F2"}
                    stroke="white"
                    strokeWidth={1.5 * delta}
                    style={{
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      filter: isHovered
                        ? `drop-shadow(0 0 ${10 * delta}px white)`
                        : isConnected
                        ? `drop-shadow(0 0 ${5 * delta}px #64B5F6)`
                        : "none",
                    }}
                    onMouseEnter={() => setHoveredPoint(pointId)}
                    onMouseLeave={() => setHoveredPoint(null)}
                    onClick={() => {
                      if (documentId && setIdWorkerShowConnections) {
                        setIdWorkerShowConnections(documentId);

                        // Добавляем текущую инициативу в историю переходов
                        if (setPrevIdWorker && finalInitiativeData) {
                          const newPrevWorker = {
                            fullName: finalInitiativeData?.name || "Инициатива",
                            documentId: finalInitiativeData?.documentId || "",
                          };
                          setPrevIdWorker([
                            ...(prevIdWorker || []),
                            newPrevWorker,
                          ]);
                        }
                      }
                    }}
                  />
                );
              });
            })}

            {sectorData.map((sector, sectorIndex) => {
              const points = createSectorPoints(
                sector.startAngle,
                sector.endAngle,
                sector.pointCount
              );
              return points.map((point, pointIndex) => {
                const pointId = `sector-${sectorIndex}-point-${pointIndex}`;
                const isHovered = hoveredPoint === pointId;
                const labelText =
                  sector.labels?.[pointIndex] || `Label ${pointIndex + 1}`;
                const fullNameText =
                  sector.fullNames?.[pointIndex] || labelText;
                const displayText = isHovered ? fullNameText : labelText;
                const isConnected = isPointConnected(labelText, hoveredPoint);

                const labelPos = createLabelPosition(
                  point.x,
                  point.y,
                  labelText
                );

                const textWidth = displayText.length * 14 * delta;
                const textHeight = 24 * delta;
                const padding = 10 * delta;
                const rectWidth = textWidth + padding * 2;
                const additionalTextHeight = isHovered
                  ? 20 * delta * 2 + padding
                  : 0;
                const rectHeight =
                  textHeight + padding * 2 + additionalTextHeight;

                const rectX = labelPos.isRightHalf
                  ? labelPos.x
                  : labelPos.x - rectWidth;
                const rectY = labelPos.y - rectHeight / 2;

                if (isHovered) return null;

                return (
                  <g key={`sector-${sectorIndex}-label-${pointIndex}`}>
                    <rect
                      x={rectX}
                      y={rectY}
                      width={rectWidth}
                      height={rectHeight}
                      rx={10 * delta}
                      ry={10 * delta}
                      fill={isConnected ? "#4A90E2" : "#2A4A63"}
                      stroke={isConnected ? "none" : "rgba(255, 255, 255, 0.2)"}
                      strokeWidth={isConnected ? 0 : 3 * delta}
                      style={{
                        transition: "all 0.3s ease",
                      }}
                    />

                    <text
                      x={rectX + padding}
                      y={
                        rectY + (textHeight + padding * 2) / 2 + textHeight / 3
                      }
                      fontSize={24 * delta}
                      fill="white"
                      textAnchor="start"
                      style={{
                        transition: "all 0.3s ease",
                      }}
                    >
                      {displayText.split("\n").map((line, index) => (
                        <tspan
                          key={index}
                          x={rectX + padding}
                          dy={index === 0 ? 0 : 24 * delta}
                        >
                          {line}
                        </tspan>
                      ))}
                    </text>
                  </g>
                );
              });
            })}

            {hoveredPoint &&
              sectorData.map((sector, sectorIndex) => {
                const points = createSectorPoints(
                  sector.startAngle,
                  sector.endAngle,
                  sector.pointCount
                );
                return points.map((point, pointIndex) => {
                  const pointId = `sector-${sectorIndex}-point-${pointIndex}`;
                  const isHovered = hoveredPoint === pointId;

                  if (!isHovered) return null;

                  const labelText =
                    sector.labels?.[pointIndex] || `Label ${pointIndex + 1}`;
                  const fullNameText =
                    sector.fullNames?.[pointIndex] || labelText;
                  const department = sector.departments?.[pointIndex] || "";
                  const jobFunction = sector.jobFunctions?.[pointIndex] || "";
                  const displayText = fullNameText;

                  const labelPos = createLabelPosition(
                    point.x,
                    point.y,
                    labelText
                  );
                  const textWidth = displayText.length * 14 * delta;
                  const textHeight = 24 * delta;
                  const padding = 10 * delta;
                  const rectWidth = textWidth + padding * 2;
                  const additionalTextHeight = 20 * delta * 2 + padding;
                  const rectHeight =
                    textHeight + padding * 2 + additionalTextHeight;

                  const rectX = labelPos.isRightHalf
                    ? labelPos.x
                    : labelPos.x - rectWidth;
                  const rectY = labelPos.y - rectHeight / 2;

                  return (
                    <g key={`hovered-${pointId}`}>
                      <rect
                        x={rectX}
                        y={rectY}
                        width={rectWidth}
                        height={rectHeight}
                        rx={10 * delta}
                        ry={10 * delta}
                        fill="#64B5F6"
                        stroke="none"
                        strokeWidth={0}
                        style={{transition: "all 0.3s ease"}}
                      />

                      <text
                        x={rectX + padding}
                        y={rectY + padding + 15 * delta}
                        fontSize={16 * delta}
                        fill="rgba(255, 255, 255, 0.8)"
                        textAnchor="start"
                      >
                        {department || "Подразделение"}
                      </text>
                      <text
                        x={rectX + padding}
                        y={rectY + rectHeight - padding - 3 * delta}
                        fontSize={16 * delta}
                        fill="rgba(255, 255, 255, 0.8)"
                        textAnchor="start"
                      >
                        {jobFunction || "Должность"}
                      </text>

                      <text
                        x={rectX + padding}
                        y={rectY + padding + 12 * delta + padding + textHeight}
                        fontSize={24 * delta}
                        fill="white"
                        textAnchor="start"
                        style={{transition: "all 0.3s ease"}}
                      >
                        {displayText.split("\n").map((line, index) => (
                          <tspan
                            key={index}
                            x={rectX + padding}
                            dy={index === 0 ? 0 : 24 * delta}
                          >
                            {line}
                          </tspan>
                        ))}
                      </text>
                    </g>
                  );
                });
              })}
          </svg>
        </div>
        <div
          style={{
            position: "absolute",
            top: "10px",
            left: "10px",
            cursor: "pointer",
            opacity: 0.9,
          }}
        ></div>
      </div>
    </>
  );
};
